import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Badge, Button, buttonClassName } from '@safetyhub/ui';
import { useAuthStore } from '../../stores/auth';
import { User } from '../../lib/auth';
import {
  invitationsApi,
  OrganizationInvitation,
  extractApiError,
  getInvitationKindLabel,
  isOwnerInvitation,
} from '../../lib/organizations';
import { formatDateToLocal } from '@safetyhub/shared';
import { AlertTriangleIcon as ExclamationIcon, CheckIcon } from '../../components/icons';

/**
 * 로그인된 사용자와 초대 대상이 일치하는지 확인
 */
function checkInvitationMatch(
  user: User,
  invitation: OrganizationInvitation
): boolean {
  // 이메일 초대인 경우
  if (invitation.email) {
    return user.email?.toLowerCase() === invitation.email.toLowerCase();
  }
  // 전화번호 초대인 경우
  if (invitation.phone) {
    return user.phone === invitation.phone;
  }
  // 아이디 초대인 경우
  if (invitation.username) {
    return user.username === invitation.username;
  }
  return false;
}

export default function AcceptInvitation() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated, fetchUser, logout } = useAuthStore();

  const [invitation, setInvitation] = useState<OrganizationInvitation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // 초대 정보 로드
  useEffect(() => {
    if (token) {
      loadInvitation();
    }
  }, [token]);

  // 계정 불일치 시 자동 로그아웃
  useEffect(() => {
    if (isAuthenticated && invitation && user) {
      const isMatch = checkInvitationMatch(user, invitation);
      if (!isMatch) {
        // 로그아웃 완료 후 페이지 새로고침 (비로그인 상태로 초대 페이지 표시)
        (async () => {
          await logout();
          window.location.reload();
        })();
      }
    }
  }, [isAuthenticated, invitation, user, logout]);

  const loadInvitation = async () => {
    if (!token) return;

    setIsLoading(true);
    setError('');
    try {
      const data = await invitationsApi.getByToken(token);
      setInvitation(data);
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!token || !isAuthenticated) return;

    setIsAccepting(true);
    setError('');
    try {
      await invitationsApi.accept(token);
      setSuccess(true);
      // 사용자 정보 새로고침 (멤버십 포함)
      await fetchUser();
      // 2초 후 대시보드로 이동
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsAccepting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto"></div>
          <p className="mt-4 text-ink-600">초대 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-card p-8 text-center">
          <div className="h-16 w-16 mx-auto rounded-full bg-red-100 flex items-center justify-center">
            <ExclamationIcon className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="mt-4 text-xl font-semibold text-ink-900">초대를 찾을 수 없습니다</h1>
          <p className="mt-2 text-ink-600">{error}</p>
          <p className="mt-4 text-sm text-ink-500">
            초대 링크가 만료되었거나 취소되었을 수 있습니다.
          </p>
          <Link
            to="/"
            className="mt-6 inline-block px-6 py-3 bg-brand-600 text-white font-medium rounded-control hover:bg-brand-700"
          >
            홈으로 이동
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-card p-8 text-center">
          <div className="h-16 w-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
            <CheckIcon className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="mt-4 text-xl font-semibold text-ink-900">초대를 수락했습니다!</h1>
          <p className="mt-2 text-ink-600">
            <strong>{invitation?.subscription?.company?.name ?? invitation?.company?.name ?? invitation?.organization?.name ?? ''}</strong> 조직에 가입되었습니다.
          </p>
          {invitation?.site && (
            <p className="mt-2 text-brand-600">
              <strong>{invitation.site.name}</strong> 현장에 할당되었습니다.
            </p>
          )}
          <p className="mt-4 text-sm text-ink-500">
            잠시 후 대시보드로 이동합니다...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-ink-50 py-12 px-4">
      <div className="max-w-md w-full bg-white shadow-lg rounded-card p-8">
        {/* 조직 정보 */}
        <div className="text-center mb-8">
          <div className="h-16 w-16 mx-auto rounded-full bg-brand-100 flex items-center justify-center">
            <BuildingIcon className="h-8 w-8 text-brand-600" />
          </div>
          <h1 className="mt-4 text-2xl font-bold text-ink-900">조직 초대</h1>
          <p className="mt-2 text-ink-600">
            <strong>{invitation?.subscription?.company?.name ?? invitation?.company?.name ?? invitation?.organization?.name ?? ''}</strong>에서 초대합니다.
          </p>
        </div>

        {/* 초대 상세 */}
        <div className="bg-ink-50 rounded-card p-4 mb-6">
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-sm text-ink-500">초대한 사람</dt>
              <dd className="text-sm font-medium text-ink-900">
                {invitation?.inviter.name}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-ink-500">역할</dt>
              <dd className="text-sm font-medium">
                {/* 소유자 초대 여부는 roleKeys(P7-b-2 — 옛 role). 구체 역할 이름은 합류 뒤 역할·선임 화면에서 */}
                <Badge tone={invitation && isOwnerInvitation(invitation) ? 'violet' : 'blue'}>
                  {invitation && getInvitationKindLabel(invitation)}
                </Badge>
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-ink-500">초대 대상</dt>
              <dd className="text-sm font-medium text-ink-900">
                {invitation?.email || invitation?.phone}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-ink-500">만료일</dt>
              <dd className="text-sm font-medium text-ink-900">
                {invitation?.expiresAt
                  ? formatDateToLocal(new Date(invitation.expiresAt))
                  : '-'}
              </dd>
            </div>
            {invitation?.site && (
              <div className="flex justify-between">
                <dt className="text-sm text-ink-500">할당 현장</dt>
                <dd className="text-sm font-medium text-ink-900">
                  {invitation.site.name}
                </dd>
              </div>
            )}
          </dl>
          {invitation?.site && (
            <p className="mt-3 text-sm text-brand-600">
              수락 시 <strong>{invitation.site.name}</strong> 현장에 자동으로 할당됩니다.
            </p>
          )}
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="mb-4 rounded-card bg-red-50 p-4">
            <div className="flex">
              <ExclamationIcon className="h-5 w-5 text-red-400" />
              <p className="ml-3 text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* 액션 버튼 */}
        {isAuthenticated ? (
          <div className="space-y-4">
            <p className="text-sm text-ink-600 text-center">
              <strong>{user?.name}</strong>님으로 로그인 중입니다.
            </p>
            <Button variant="primary" size="lg" onClick={handleAccept} disabled={isAccepting} className="w-full">
              {isAccepting ? '수락 중...' : '초대 수락'}
            </Button>
            <Link
              to={`/login?redirect=/invite/${token}`}
              className={buttonClassName('secondary', 'lg', 'w-full')}
            >
              다른 계정으로 로그인
            </Link>
          </div>
        ) : invitation?.isUserRegistered ? (
          // 기존 사용자: 로그인 유도
          <div className="space-y-4">
            <div className="rounded-card bg-blue-50 p-4">
              <p className="text-sm text-blue-700 text-center">
                <strong>{invitation.email || invitation.phone}</strong> 계정이 이미 등록되어 있습니다.
              </p>
            </div>
            <Link
              to={`/login?redirect=/invite/${token}`}
              className={buttonClassName('primary', 'lg', 'w-full')}
            >
              로그인하고 수락하기
            </Link>
          </div>
        ) : (
          // 미가입 사용자: 회원가입 유도 (가입 시 자동 수락)
          // 초대 타입에 따라 가입 방식 고정: 이메일 초대 → 이메일 가입, 전화번호 초대 → 아이디 가입
          <div className="space-y-4">
            <div className="rounded-card bg-green-50 p-4">
              <p className="text-sm text-green-700 text-center">
                등록된 계정이 없습니다.<br />
                회원가입 완료 시 <strong>자동으로 초대가 수락</strong>됩니다.
              </p>
            </div>
            <Link
              to={invitation?.email
                ? `/signup/email/complete?inviteToken=${token}&email=${encodeURIComponent(invitation.email)}`
                : `/signup/username?inviteToken=${token}&phone=${encodeURIComponent(invitation?.phone || '')}`
              }
              className={buttonClassName('success', 'lg', 'w-full')}
            >
              {invitation?.email ? '이메일로 가입하고 수락하기' : '아이디로 가입하고 수락하기'}
            </Link>
            <p className="text-sm text-ink-500 text-center">
              이미 계정이 있으신가요?{' '}
              <Link to={`/login?redirect=/invite/${token}`} className="text-brand-600 hover:underline">
                로그인
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Icons
function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  );
}
